import Vue from 'vue';

import { ReadyType } from '@/stores/ready.store';
import { SnowplowContext } from './snowplow-contexts';
import { TrackingProvider, TrackingProviderPropertiesInterface } from './tracking-provider';
import { getUtmSearchParams } from '@/helpers/composables/useArbitrage';
import type { Store } from 'vuex';

let lastPath = '';

async function tracker(...args: any) {
	if (typeof window !== 'undefined') {
		await Vue.$jw.ready?.waitFor(ReadyType.SNOWPLOW_INIT);

		try {
			window.snowplow(...args);
		} catch (err) {
			console.error('[snowplow] function not found in window object', err);
		}
	}
}

const attachSnowplow = (p: any, l: any, o: any, w: any, i: any, n?: any, g?: any) => {
	if (!p[i]) {
		p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace || [];
		p.GlobalSnowplowNamespace.push(i);
		p[i] = function () {
			(p[i].q = p[i].q || []).push(arguments);
		};
		p[i].q = p[i].q || [];
		n = l.createElement(o);
		g = l.getElementsByTagName(o)[0];
		n.async = 1;
		n.defer = 1;
		n.src = w;
		g.parentNode.insertBefore(n, g);
	}
};

interface SnowplowConfig {
	userId: string;
	appId: string;
	cookieDomain: string;
	platform: 'web' | 'mob';
	doNotTrack: boolean;
	eventMethod: string;
	postPath: string;
}

export class TrackingProviderSnowplow extends TrackingProvider {
	dataProcessingService = null;
	private store: Store<any> | null = null;

	constructor(private config: SnowplowConfig) {
		super();

		// config validation
		if (!this.config.appId) {
			throw new Error('Expected "config.appId"');
		}
		if (!this.config.cookieDomain) {
			throw new Error('Expected "config.cookieDomain"');
		}
	}

	/**
	 * Set the Vuex store instance for accessing routing state
	 */
	setStore(store: Store<any>) {
		this.store = store;
	}

	/**
	 * Helper to get the domain dynamically based on activeDomain from the store
	 */
	private getDomain(): string {
		if (!this.store) {
			// Fallback to www if store is not available
			return `https://www.${JW_CONFIG.DOMAIN}`;
		}
		const activeDomain = this.store.getters['routing/activeDomain'] || 'www';
		return `https://${activeDomain}.${JW_CONFIG.DOMAIN}`;
	}

	initialize() {
		// failsafe for SSR
		if (typeof window === 'undefined') {
			return Promise.resolve();
		}

		return new Promise<void>(async (resolve, reject) => {
			// ignore config.doNotTrack; it's first party data
			const splib = `/${ASSETS_DIR}/js/core-380.min.js`;

			if (!('snowplow' in window)) {
				attachSnowplow(window, document, 'script', splib, 'snowplow');
			}

			tracker('newTracker', 'cf', `https://${JW_CONFIG.SNOWPLOW_COLLECTOR_ENDPOINT}`, this.config);

			tracker('setUserId', this.config.userId);

			Vue.$jw.ready?.setReady(ReadyType.SNOWPLOW_INIT);

			resolve();
		});
	}

	async trackPage({ path, title, contexts = [] }: { path?: string; title?: string; contexts: SnowplowContext[] }) {
		const utmSearchParams = getUtmSearchParams();
		const DOMAIN = this.getDomain();

		if (lastPath) {
			const url = applySearchParams(new URL(`${DOMAIN}${lastPath}`), utmSearchParams);
			tracker('setReferrerUrl', url.href);
		}

		const url = applySearchParams(new URL(`${DOMAIN}${path}`), utmSearchParams);
		tracker('setCustomUrl', url.href);
		tracker('trackPageView', {
			title,
			context: contexts.map(c => c.toObject()),
		});

		lastPath = path ? path : '';
	}

	refreshPermanentAudiences(permanent_audiences: string[] = []) {}
	refreshPermanentAudiencesSubgenres(permanent_audiences: string[]): void {}

	async trackEvent(category: string, properties: TrackingProviderPropertiesInterface, contexts: SnowplowContext[]) {
		const DOMAIN = this.getDomain();

		if (lastPath) {
			tracker('setReferrerUrl', `${DOMAIN}${lastPath}`);
		}

		const { action, label, property, value } = properties;

		tracker('trackStructEvent', {
			category,
			action,
			label,
			property,
			value,
			context: contexts.map(c => c.toObject()),
		});
	}
}

function applySearchParams(url: URL, params: URLSearchParams | null) {
	if (params) {
		params.forEach((value, key) => url.searchParams.set(key, value));
	}

	return url;
}
